import React, { useState, FormEvent, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { Loader2, Mail, Key, AlertCircle, CheckCircle2, Info, Layers, UserPlus, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import OptionalInfoForm from "@/components/OptionalInfoForm";
import SnapLayerHero from "@/components/layout/SnapLayerHero";

type LicenseSummary = {
  id: string;
  code: string;
  plan: string;
  status: string;
  credits: number;
};

type LicenseInfo = {
  found: boolean;
  total_count: number;
  available_count: number;
  licenses?: Array<LicenseSummary>; // Optional - may not be present in new format
  entered_license?: { // New format - only shows entered license, not all codes
    id: string;
    code: string;
    plan: string;
    status: string;
    credits: number;
    purchase_id: string;
  };
  licenses_summary?: { // Summary info without exposing all codes
    count: number;
    available_count: number;
    plan_id: string;
    credits_per_license: number;
  };
  purchase_email?: string;
  stack_size?: number;
  stack_available_count?: number;
  is_stack?: boolean;
  activation_email_matches_purchase?: boolean;
  requires_owner_verification?: boolean;
};

const Index = () => {
  const { user, loading } = useAuth();
  const [licenseKey, setLicenseKey] = useState("");
  const [email, setEmail] = useState("");
  const [purchaseEmail, setPurchaseEmail] = useState("");
  const [useDifferentEmail, setUseDifferentEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [facebookName, setFacebookName] = useState("");
  const [discordHandle, setDiscordHandle] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);

  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [stackActivating, setStackActivating] = useState(false);
  const [directActivating, setDirectActivating] = useState(false);
  const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | null>(null);

  const getAvailableLicenses = (info: LicenseInfo | null): LicenseSummary[] => {
    if (!info) return [];
    // If we have the entered_license (new format), use that
    if (info.entered_license) {
      const status = (info.entered_license.status || '').toLowerCase();
      const isActivatable = status === 'valid' || (status === 'active' && info.available_count > 0);
      if (isActivatable) {
        // Return a summary object - we don't need actual codes until activation
        return [{
          id: info.entered_license.id,
          code: info.entered_license.code, // This is the entered code, not all codes
          plan: info.entered_license.plan || '',
          status: info.entered_license.status || 'valid',
          credits: info.entered_license.credits || 0,
        }];
      }
      return [];
    }
    // Fallback to old format for backwards compatibility
    if (info.licenses) {
      return info.licenses.filter((license) => {
        const status = (license.status || '').toLowerCase();
        return status === 'valid' || (status === 'active' && info.available_count > 0);
      });
    }
    return [];
  };

  const resetActivationState = () => {
    setLicenseKey("");
    setEmail("");
    setNewEmail("");
    setPurchaseEmail("");
    setLicenseInfo(null);
    setUseDifferentEmail(false);
    setPhoneNumber("");
    setFacebookName("");
    setDiscordHandle("");
    setMarketingConsent(false);
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-sl-bg text-sl-text flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sl-accent" />
      </main>
    );
  }

  const handleCheckCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!licenseKey.trim()) {
      toast.error("Please enter a license key");
      return;
    }

    try {
      setChecking(true);
      // Reset state before new check
      setLicenseInfo(null);
      setPurchaseEmail("");
      setEmail("");
      setUseDifferentEmail(false);
      setNewEmail("");

      const { data, error } = await supabase.functions.invoke("check-license-code", {
        body: { licenseKey: licenseKey.trim() },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setLicenseInfo(data);
      if (data.found && data.purchase_email) {
        setPurchaseEmail(data.purchase_email);
        setEmail(data.purchase_email);

        // If email matches purchase email, try to prefill optional info
        if (data.activation_email_matches_purchase && data.can_prefill_optional_info) {
          try {
            const { data: optionalData, error: optionalError } = await supabase.functions.invoke("get-optional-info", {
              body: {
                licenseKey: licenseKey.trim(),
                email: data.purchase_email,
              },
            });

            if (!optionalError && optionalData?.allowed && optionalData?.optional_info) {
              const info = optionalData.optional_info;
              if (info.phone_number) setPhoneNumber(info.phone_number);
              if (info.facebook_name) setFacebookName(info.facebook_name);
              if (info.discord_handle) setDiscordHandle(info.discord_handle);
              if (info.marketing_consent !== null) setMarketingConsent(info.marketing_consent === true);
            }
          } catch (err) {
            // Silently fail - prefilling is optional
            console.log("Optional info prefilling failed:", err);
          }
        }
      }
      if (!data.found) {
        toast.error("License key not found");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to check license key");
    } finally {
      setChecking(false);
    }
  };

  const handleSendActivation = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !licenseInfo) {
      toast.error("Please enter your email");
      return;
    }

    const availableLicenses = getAvailableLicenses(licenseInfo);
    if (availableLicenses.length === 0) {
      toast.error("No available licenses to activate");
      return;
    }

    // If using different email, we need to send confirmation to purchase email first
    if (useDifferentEmail && newEmail.trim() && purchaseEmail) {
      // This will be handled by a new Edge Function
      try {
        setSending(true);
        const { data, error } = await supabase.functions.invoke("request-email-change", {
          body: {
            purchaseEmail: purchaseEmail.trim(),
            newEmail: newEmail.trim(),
            licenseCodes: availableLicenses.map((l) => l.code),
            activateAll: availableLicenses.length > 1,
            phoneNumber: phoneNumber.trim() || null,
            facebookName: facebookName.trim() || null,
            discordHandle: discordHandle.trim() || null,
            marketingConsent,
          },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        toast.success("Email change confirmation sent! Check your purchase email inbox.");
        resetActivationState();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send email change confirmation");
      } finally {
        setSending(false);
      }
      return;
    }

    // Standard activation flow
    try {
      setSending(true);
      // Use available licenses if we have them, otherwise use the entered license key
      const licenseCodes = availableLicenses.length > 0
        ? availableLicenses.map((l) => l.code)
        : licenseInfo?.entered_license?.code
          ? [licenseInfo.entered_license.code]
          : [licenseKey.trim()];

      const { data, error } = await supabase.functions.invoke("send-activation-email", {
        body: {
          email: email.trim(),
          licenseCodes,
          activateAll: (licenseInfo?.is_stack && (licenseInfo?.stack_size || 0) > 1) || availableLicenses.length > 1,
          phoneNumber: phoneNumber.trim() || null,
          facebookName: facebookName.trim() || null,
          discordHandle: discordHandle.trim() || null,
          marketingConsent,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success("Activation email sent! Check your inbox.");
      resetActivationState();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send activation email");
    } finally {
      setSending(false);
    }
  };

  const handleActivateStack = async () => {
    if (!licenseInfo || !licenseInfo.is_stack || licenseInfo.available_count < 1) {
      toast.error("No available licenses in this stack to activate.");
      return;
    }
    if (!purchaseEmail) {
      toast.error("Purchase email is required to activate the entire stack.");
      return;
    }
    if (!licenseKey.trim()) {
      toast.error("License key is missing. Please re-enter your key.");
      return;
    }

    try {
      setStackActivating(true);
      const { data, error } = await supabase.functions.invoke("activate-all-stack", {
        body: {
          licenseKey: licenseKey.trim(),
          purchaseEmail: purchaseEmail.trim(),
          optionalInfo: {
            phoneNumber: phoneNumber.trim() || null,
            facebookName: facebookName.trim() || null,
            discordHandle: discordHandle.trim() || null,
            marketingConsent,
          },
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success("Stack activation requested! Check your email to confirm.");
      resetActivationState();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to start stack activation");
    } finally {
      setStackActivating(false);
    }
  };

  const handleDirectActivation = async () => {
    if (!licenseInfo) {
      toast.error("Please check your license key first.");
      return;
    }
    if (!user) {
      toast.error("Sign in to add a license directly to your account.");
      return;
    }
    if (!licenseKey.trim()) {
      toast.error("License key is missing. Please re-enter your key.");
      return;
    }
    if (licenseInfo.available_count < 1) {
      toast.error("No available licenses to add right now.");
      return;
    }

    try {
      setDirectActivating(true);
      const { data, error } = await supabase.functions.invoke("activate-license", {
        body: {
          licenseKey: licenseKey.trim(),
          activationPath: "incremental",
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success("License added to your account!");
      resetActivationState();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to activate license");
    } finally {
      setDirectActivating(false);
    }
  };

  return (
    <main className="min-h-screen bg-sl-bg text-sl-text">
      <SnapLayerHero />

      {/* Activation Section */}
      <div className="container mx-auto px-6 py-12 max-w-2xl">
        <div className="bg-sl-surface border border-sl-surface-2 rounded-2xl p-8 space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold">Activate Your Account</h2>
            <p className="text-sl-muted">
              Enter your license key to get started, or retrieve all your codes by email
            </p>
          </div>

          {user && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 space-y-3 text-green-50">
              <p className="text-sm">
                Signed in as <strong>{user.email ?? "SnapLayer user"}</strong>. Add another license below or jump back to your dashboard anytime.
              </p>
              <Button
                asChild
                className="w-full bg-green-500 hover:bg-green-500/90 text-white font-semibold border border-green-400/70"
              >
                <Link to="/dashboard">Go to Dashboard</Link>
              </Button>
            </div>
          )}

          {/* License Key Form */}
          <form onSubmit={handleCheckCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                <Key className="h-4 w-4" />
                License Key
              </label>
              <input
                type="text"
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value)}
                placeholder="Enter your license key"
                className="w-full px-4 py-3 bg-sl-bg border border-sl-surface-2 rounded-lg focus:outline-none focus:border-sl-accent text-sl-text"
                disabled={checking}
              />
            </div>
            <Button
              type="submit"
              disabled={checking || !licenseKey.trim()}
              className="w-full bg-sl-accent hover:bg-sl-accent/90"
            >
              {checking ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Checking...
                </>
              ) : (
                "Check License"
              )}
            </Button>
          </form>

          {/* Or Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-sl-surface-2"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-sl-surface text-sl-muted">OR</span>
            </div>
          </div>

          {/* Retrieve by Email */}
          <div className="text-center">
            <Button
              asChild
              variant="outline"
              className="w-full bg-sl-bg text-white border-2 border-sl-accent/60 hover:border-sl-accent hover:bg-sl-accent/10 font-semibold"
            >
              <Link to="/retrieve-codes">
                <Mail className="h-4 w-4 mr-2" />
                Retrieve All Codes by Email
              </Link>
            </Button>
          </div>

          {/* License Info & Activation Form */}
          {licenseInfo?.found && (
            <div className="bg-sl-surface-2 border border-sl-accent/30 rounded-lg p-6 space-y-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2">License Found</h3>
                  <p className="text-sm text-sl-muted mb-4">
                    Found <strong>{licenseInfo.total_count}</strong> license(s) for this key.{" "}
                    <strong>{licenseInfo.available_count}</strong> available to activate.
                  </p>

                  {licenseInfo.available_count === 0 && (
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <p className="font-medium text-yellow-300 mb-1">No licenses available to activate</p>
                          <p className="text-sl-muted">
                            All licenses for this key have already been activated or revoked.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {licenseInfo.total_count > 1 && licenseInfo.available_count > 0 && (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-2">
                        <Info className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <p className="font-medium text-blue-300 mb-2">Multiple licenses detected</p>
                          <p className="text-sl-muted mb-2">
                            You can activate all licenses to your account now. Remember:
                          </p>
                          <ul className="list-disc list-inside text-sl-muted space-y-1 ml-2">
                            <li>Licenses are not locked to an email and can be sold/transferred individually at any time</li>
                            <li>You can change the email on your whole account at any time</li>
                            <li>You don't need to use vendor emails or create separate accounts</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-5">
                {licenseInfo.is_stack && licenseInfo.available_count > 1 && (
                  <div className="border border-sl-accent/25 bg-sl-bg/40 rounded-xl p-5 shadow-sm space-y-3">
                    <div className="flex items-start gap-4">
                      <Layers className="h-6 w-6 text-sl-accent flex-shrink-0" />
                      <div className="space-y-1">
                        <h4 className="text-lg font-semibold">Activate All Licenses in Stack</h4>
                        <p className="text-sm text-sl-muted">
                          Instantly send a confirmation email to <strong>{purchaseEmail ?? "your purchase email"}</strong> and link every license in this stack
                          ({licenseInfo.available_count} available) once confirmed.
                        </p>
                        <p className="text-xs text-sl-muted">
                          Best when you want to keep the stack together or activate on behalf of the original purchaser.
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      disabled={stackActivating || !purchaseEmail}
                      onClick={handleActivateStack}
                      className="w-full bg-sl-accent hover:bg-sl-accent/90"
                    >
                      {stackActivating ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                          Requesting Stack Activation...
                        </>
                      ) : (
                        "Activate Entire Stack"
                      )}
                    </Button>
                  </div>
                )}

                {user && (
                  <div className="border border-sl-surface-2 bg-sl-bg/40 rounded-xl p-5 shadow-sm space-y-3">
                    <div className="flex items-start gap-4">
                      <UserPlus className="h-6 w-6 text-green-400 flex-shrink-0" />
                      <div className="space-y-1">
                        <h4 className="text-lg font-semibold">Add Another License</h4>
                        <p className="text-sm text-sl-muted">
                          Attach this license directly to your SnapLayer account{user.email ? ` (${user.email})` : ""} without waiting for email confirmation.
                        </p>
                        <p className="text-xs text-sl-muted">
                          You must be signed in and the license must still be available.
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={directActivating || licenseInfo.available_count < 1}
                      onClick={handleDirectActivation}
                      className="w-full border-green-400/60 text-green-200 hover:bg-green-500/10"
                    >
                      {directActivating ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                          Adding License...
                        </>
                      ) : (
                        "Add to My Account"
                      )}
                    </Button>
                  </div>
                )}

                <div className="border border-sl-surface-2 bg-sl-bg/40 rounded-xl p-5 shadow-sm">
                  <div className="flex items-start gap-4">
                    <Send className="h-6 w-6 text-blue-300 flex-shrink-0" />
                    <div className="space-y-1">
                      <h4 className="text-lg font-semibold">Activate License Now</h4>
                      <p className="text-sm text-sl-muted">
                        Choose where you want the activation email sent. You can keep the purchase email or request a new one.
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleSendActivation} className="space-y-4 mt-4">
                    {/* Email Selection */}
                    {purchaseEmail && (
                      <div className="space-y-3">
                        <label className="block text-sm font-medium">Email Address</label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="emailChoice"
                              checked={!useDifferentEmail}
                              onChange={() => {
                                setUseDifferentEmail(false);
                                setEmail(purchaseEmail);
                              }}
                              className="text-sl-accent"
                            />
                            <span className="text-sm">
                              Use purchase email: <strong>{purchaseEmail}</strong>
                            </span>
                          </label>
                          <label className="flex items-start gap-2 cursor-not-allowed opacity-60">
                            <input
                              type="radio"
                              name="emailChoice"
                              disabled
                              className="text-sl-accent mt-1"
                            />
                            <div className="text-sm">
                              <span>Use a different email</span>
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-500/30 text-amber-300 border border-amber-500/50">
                                Coming Soon
                              </span>
                              <p className="text-xs text-muted-foreground mt-1">
                                Email changes are not available during beta. Contact support if you need assistance.
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>
                    )}

                    {!purchaseEmail && (
                      <div>
                        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Your Email
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="w-full px-4 py-3 bg-sl-bg border-2 border-sl-accent/50 rounded-lg focus:outline-none focus:border-sl-accent text-sl-text"
                          required
                          disabled={sending}
                        />
                      </div>
                    )}

                    {/* Optional Fields */}
                    <OptionalInfoForm
                      phoneNumber={phoneNumber}
                      facebookName={facebookName}
                      discordHandle={discordHandle}
                      marketingConsent={marketingConsent}
                      onPhoneNumberChange={setPhoneNumber}
                      onFacebookNameChange={setFacebookName}
                      onDiscordHandleChange={setDiscordHandle}
                      onMarketingConsentChange={setMarketingConsent}
                      disabled={sending}
                    />

                    <Button
                      type="submit"
                      disabled={sending || !email.trim() || (licenseInfo?.available_count ?? 0) < 1}
                      className="w-full bg-sl-accent hover:bg-sl-accent/90"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                          Sending Activation Email...
                        </>
                      ) : (
                        "Send Activation Email"
                      )}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Already have account */}
          <div className="text-center pt-4 border-t border-sl-surface-2">
            <p className="text-sm text-sl-muted">
              Already activated?{" "}
              <Link to="/login" className="text-blue-400 hover:text-blue-300 hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Index;
